import React from 'react'
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react'
import { Button } from './button'
import { friendlyError } from '@/lib/friendly-error'

/**
 * Reusable friendly error / failure state. Use anywhere a query or action fails
 * to show a human FR message (never a raw stack trace) + an optional retry.
 *
 *   <ErrorState error={query.error} onRetry={() => query.refetch()} />
 */
export interface ErrorStateProps {
  error?: unknown
  title?: string
  /** Overrides the derived friendly message. */
  message?: string
  onRetry?: () => void
  retryLabel?: string
  className?: string
  compact?: boolean
}

export function ErrorState({
  error,
  title = 'Une erreur est survenue',
  message,
  onRetry,
  retryLabel = 'Réessayer',
  className,
  compact,
}: ErrorStateProps) {
  const humanMessage = message || friendlyError(error, title)
  return (
    <div
      className={
        'flex flex-col items-center justify-center gap-3 text-center ' +
        (compact ? 'py-8 ' : 'py-12 ') +
        (className ?? '')
      }
      data-testid="error-state"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10">
        <AlertTriangle className="h-6 w-6 text-destructive" />
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">{humanMessage}</p>
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          {retryLabel}
        </Button>
      )}
    </div>
  )
}

/**
 * 3-way query-state wrapper. Renders loading / error+retry / children so a page's
 * content region never gates on a blocking `if (isLoading) return <spinner>` with
 * no error branch (the systemic defect audited in AUDIT_LOADING_ERROR_WEB.md).
 * Keep the page header + primary action OUTSIDE this so they always render.
 *
 *   <QueryState loading={q.isLoading} error={q.isError && q.error} onRetry={() => q.refetch()}>
 *     …content…
 *   </QueryState>
 */
export interface QueryStateProps {
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  loadingFallback?: React.ReactNode
  errorTitle?: string
  errorMessage?: string
  compact?: boolean
  className?: string
  children: React.ReactNode
}

export function QueryState({
  loading,
  error,
  onRetry,
  loadingFallback,
  errorTitle,
  errorMessage,
  compact,
  className,
  children,
}: QueryStateProps) {
  if (loading)
    return (
      <>
        {loadingFallback ?? (
          <div
            data-testid="loading-state"
            className={'flex items-center justify-center ' + (compact ? 'py-8 ' : 'py-16 ') + (className ?? '')}
          >
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}
      </>
    )
  if (error)
    return (
      <ErrorState
        error={error}
        title={errorTitle}
        message={errorMessage}
        onRetry={onRetry}
        compact={compact}
        className={className}
      />
    )
  return <>{children}</>
}

interface Props {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)

    // Auto-reload on chunk load failures (e.g. after a new deployment)
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('Unable to preload CSS')
    if (isChunkError) {
      const reloadKey = `chunk-reload-${Date.now()}`
      try {
        if (!sessionStorage.getItem('chunk-reloaded')) {
          sessionStorage.setItem('chunk-reloaded', reloadKey)
          window.location.reload()
        }
      } catch { /* storage unavailable (sandboxed/opaque origin) */ }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      const isChunkError =
        this.state.error?.name === 'ChunkLoadError' ||
        this.state.error?.message.includes('Failed to fetch dynamically imported module') ||
        this.state.error?.message.includes('Importing a module script failed')

      if (isChunkError) {
        return (
          <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/10">
              <RotateCcw className="h-7 w-7 text-amber-500" />
            </div>
            <h2 className="text-lg font-semibold">Mise à jour disponible</h2>
            <p className="max-w-md text-center text-sm text-muted-foreground">
              Une nouvelle version de l'application est disponible. Rechargez la page pour continuer.
            </p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Recharger la page
            </Button>
          </div>
        )
      }

      // Friendly, human message — NEVER a raw stack trace / "Internal Server Error".
      return (
        <div className="flex h-full items-center justify-center p-8">
          <ErrorState
            error={this.state.error}
            title="Une erreur est survenue"
            onRetry={() => this.setState({ hasError: false, error: null })}
          />
        </div>
      )
    }
    return this.props.children
  }
}
